// ============================================================
// sheetPatches - the partials a sheet editor hands to `onChange`.
//
// One home for the edits both hosts of the sheet make (the live tab and
// the library editor), so a rule that rides on an edit - Agility
// re-deriving movement - cannot be kept by one and forgotten by the
// other. Pure: each returns a NEW partial and never touches the sheet.
// ============================================================
import type { Character, CharacteristicId } from './types';
import { characteristicBonus } from './combatStatsResolver';

export { CHARACTERISTICS } from './characteristicList';

/** The four paces of movement, slowest first. */
export const PACES = ['half', 'full', 'charge', 'run'] as const;

/** Movement in metres from the Agility bonus: half, full, charge, run. */
export function movementFor(agility: number): Character['movement'] {
  const bonus = characteristicBonus(agility);
  return { half: bonus, full: bonus * 2, charge: bonus * 3, run: bonus * 6 };
}

/**
 * The partial for one characteristic edit. Agility's value also
 * re-derives movement from the new Agility Bonus.
 */
export function characteristicPatch(
  sheet: Pick<Character, 'characteristics'>,
  id: CharacteristicId,
  field: 'value' | 'unnaturalMultiplier',
  value: number,
): Partial<Character> {
  const characteristics = { ...sheet.characteristics, [id]: { ...sheet.characteristics[id], [field]: value } };
  // Only a CHANGED Agility: a field that re-emits its own value on blur
  // must not wipe movement that was set by hand.
  const moves = id === 'agi' && field === 'value' && value !== sheet.characteristics.agi.value;
  return moves ? { characteristics, movement: movementFor(value) } : { characteristics };
}
